import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  getDocs
} from "firebase/firestore";
import { DateTime } from "luxon";
import * as XLSX from "xlsx";
import {
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import WarningIcon from "@mui/icons-material/Warning";
import RefreshIcon from "@mui/icons-material/Refresh";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

const theme = createTheme({
  palette: {
    primary: {
      main: "#dc2626", // red-600
    },
    secondary: {
      main: "#eab308", // yellow-500
    },
  },
});

const TodayInvoicesGrid = () => {
  const [data, setData] = useState([]);
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState("all"); // Filter state

  // Get UK timezone date range (today only)
  const getUKDateRange = () => {
    const now = DateTime.now().setZone("Europe/London");
    const startOfDay = now.startOf("day");
    const endOfDay = now.endOf("day");

    return {
      start: startOfDay.toISO(),
      end: endOfDay.toISO()
    };
  };

  // Get last 3 days in UK timezone
  // const getUKDateRange = () => {
  //   const now = DateTime.now().setZone("Europe/London");
  //   const startOfRange = now.minus({ days: 3 }).startOf("day");
  //   const endOfRange = now.endOf("day");

  //   return {
  //     start: startOfRange.toISO(),
  //     end: endOfRange.toISO()
  //   };
  // };

  const downloadGridPDF = () => {
    try {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a3",
      });

      const headers = ["Item", ...filteredData.map(d => d.restaurantName), "Total"];
      const rows = [];

      Object.entries(items).forEach(([category, categoryItems]) => {
        rows.push([category, ...Array(headers.length - 1).fill("")]);

        categoryItems.forEach(item => {
          const row = [item.title];
          let rowTotal = 0;

          filteredData.forEach(restaurant => {
            const quantity = restaurant.itemQuantities.find(
              q => q.title === item.title
            )?.quantity || 0;
            row.push(quantity);
            rowTotal += quantity;
          });

          row.push(rowTotal);
          rows.push(row);
        });
      });

      const totalRow = ["Total"];
      let grandTotal = 0;

      filteredData.forEach(restaurant => {
        const restaurantTotal = restaurant.itemQuantities.reduce(
          (sum, item) => sum + item.quantity,
          0
        );
        totalRow.push(restaurantTotal);
        grandTotal += restaurantTotal;
      });

      totalRow.push(grandTotal);
      rows.push(totalRow);

      doc.setFontSize(24);
      doc.text(
        "Today's Invoice Grid",
        doc.internal.pageSize.getWidth() / 2,
        15,
        { align: "center" }
      );

      doc.setFontSize(12);
      doc.text(
        `Last updated: ${lastUpdated?.toLocaleString() || "Unknown"}`,
        10,
        25
      );

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 30,
        theme: "grid",
        headStyles: {
          fillColor: [220, 38, 38],
          fontSize: 14,
          fontStyle: "bold",
        },
        bodyStyles: {
          fontSize: 12,
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250],
        },
        pageBreak: "auto",
        showHead: "everyPage",
      });

      doc.save(`todays_invoices_${new Date().toISOString()}.pdf`);
    } catch (err) {
      setError("Failed to generate Grid PDF");
      console.error("Grid PDF error:", err);
    }
  };



  const downloadDispatchNotesPDF = () => {
    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      let isFirstPage = true;

      data.forEach((restaurant) => {
        const { restaurantName, itemQuantities } = restaurant;

        if (!isFirstPage) {
          doc.addPage();
        }
        isFirstPage = false;

        const orderDate = lastUpdated
          ? lastUpdated.toLocaleDateString()
          : "";

        // === BUILD COMPLETE TABLE ===
        const tableRows = [];

        // Row 1: Header Info (6 columns)
        tableRows.push([
          {
            content: `Restaurant Name:\n${restaurantName}`,
            colSpan: 2,
            styles: { fontStyle: "bold", halign: "center", valign: "middle" },
          },
          {
            content: `Dispatch Time: _______\nReceived Time: _______`,
            colSpan: 1,
            styles: { halign: "center", valign: "middle" },
          },
          {
            content: `Order Date:\n${orderDate}\nDelivery Date: _______`,
            colSpan: 1,
            styles: { halign: "center", valign: "middle" },
          },
          {
            content: `Document No: _______\nIssue Date: _______\nRevision No: _______`,
            colSpan: 1,
            styles: { halign: "center", valign: "middle" },
          },
          {
            content: `Created by:\nFoodsure Pvt Ltd\nApproved by: _______`,
            colSpan: 1,
            styles: { halign: "center", valign: "middle" },
          },
        ]);

        // Row 2: Vehicle Info
        tableRows.push([
          {
            content: `Vehicle No: _______`,
            colSpan: 2,
            styles: { halign: "center", valign: "middle" },
          },
          {
            content: `DELIVERY MONITORING RECORD`,
            colSpan: 3,
            styles: { fontStyle: "bold", halign: "center", valign: "middle" },
          },
          {
            content: `Vehicle Temperature: _______`,
            colSpan: 1,
            styles: { halign: "center", valign: "middle" },
          },
        ]);

        // Row 3: Empty spacer row
        tableRows.push([
          { content: "", colSpan: 6, styles: { minHeight: 5 } },
        ]);

        // Row 4: Table Header
        tableRows.push([
          { content: "S.NO", styles: { fontStyle: "bold", halign: "center" } },
          { content: "Product Name", styles: { fontStyle: "bold", halign: "left" } },
          { content: "Quantity in\nunits", styles: { fontStyle: "bold", halign: "center" } },
          { content: "BATCH NO", styles: { fontStyle: "bold", halign: "center" } },
          { content: "BATCH NO", styles: { fontStyle: "bold", halign: "center" } },
          { content: "Remarks/corrective\naction", styles: { fontStyle: "bold", halign: "left" } },
        ]);

        // === ADD ITEMS ===
        let serial = 1;
        let totalQuantity = 0;

        itemQuantities.forEach((iq) => {
          const qty = Number(iq.quantity) || 0;
          if (qty <= 0) return;

          totalQuantity += qty;

          tableRows.push([
            { content: serial.toString(), styles: { halign: "center" } },
            { content: iq.title, styles: { halign: "left" } },
            { content: qty.toString(), styles: { halign: "center" } },
            { content: "", styles: { halign: "center" } },
            { content: "", styles: { halign: "center" } },
            { content: "", styles: { halign: "left" } },
          ]);

          serial += 1;
        });

        // === TOTAL ROW ===
        tableRows.push([
          { content: "", styles: { halign: "center" } },
          { content: "TOTAL UNIT", styles: { fontStyle: "bold", halign: "left" } },
          { content: totalQuantity.toString(), styles: { fontStyle: "bold", halign: "center" } },
          { content: "", styles: { halign: "center" } },
          { content: "", styles: { halign: "center" } },
          { content: "", styles: { halign: "left" } },
        ]);

        // === DRIVER SIGNATURE ROW ===
        tableRows.push([
          { content: "", styles: { halign: "center" } },
          { content: "DRIVER SIGNATURE", styles: { fontStyle: "bold", halign: "left" } },
          { content: "", colSpan: 4, styles: { minHeight: 20 } },
        ]);

        // === CREATE TABLE ===
        autoTable(doc, {
  body: tableRows,
  startY: 10,
  theme: "grid",
  styles: {
    fontSize: 8.5,
    overflow: "linebreak",
    valign: "middle",
    cellPadding: 3,
    lineColor: [0, 0, 0],
    lineWidth: 0.5,
  },
  bodyStyles: {
    fontSize: 8.5,
  },
  margin: { top: 10, right: 10, bottom: 10, left: 10 },
  columnStyles: {
    0: { cellWidth: 15, halign: "center" },      // S.NO
    1: { cellWidth: 60, halign: "left" },        // Product Name
    2: { cellWidth: 24, halign: "center" },      // Quantity
    3: { cellWidth: 24, halign: "center" },      // BATCH NO 1
    4: { cellWidth: 24, halign: "center" },      // BATCH NO 2
    5: { cellWidth: 41, halign: "left" },        // Remarks
  },
});

      });

      doc.save(`dispatch_notes_${new Date().toISOString()}.pdf`);
    } catch (err) {
      setError("Failed to generate Dispatch Notes PDF");
      console.error("Dispatch notes PDF error:", err);
    }
  };





  const downloadDispatchNotesExcel = () => {
    try {
      const workbook = XLSX.utils.book_new();

      // For each restaurant, create a separate sheet
      data.forEach((restaurant) => {
        const sheetData = [];
        const { restaurantName, itemQuantities } = restaurant;

        // === TOP ROW (Row 1) ===
        // Col A: empty
        // Col B: Restaurant Name
        // Col C: "Dispatch Time / Received Time:" (left empty for user)
        // Col D: "Order Date / Delivery Date:"
        // Col E: "Document No / Issue Date / Revision No:"
        // Col F: "Created by / Approved by:"
        const orderDate = lastUpdated
          ? lastUpdated.toLocaleDateString()
          : ""; // you can change to invoice date if you have it

        sheetData.push([
          "", // A1 empty
          `Restaurant Name: ${restaurantName}`, // B1
          "Dispatch Time: ______   Received Time: ______", // C1
          `Order Date: ${orderDate}   Delivery Date: ______`, // D1
          "Document No: ______   Issue Date: ______   Revision No: ______", // E1
          "Created by: Fooddsure Pvt Ltd   Approved by: ______", // F1
        ]);

        // === SECOND ROW (Row 2) ===
        // Col A: empty
        // Col B: "Vehicle No:"
        // Col C: "Delivery monitoring record"
        // Col D: "Vehicle Temperature:"
        sheetData.push([
          "", // A2 empty
          "Vehicle No: ______", // B2
          "DELIVERY MONITORING RECORD", // C2
          "Vehicle Temperature: ______", // D2
        ]);

        // Empty spacer row (Row 3)
        sheetData.push([]);

        // === HEADER ROW for actual table (Row 4) ===
        // 1. S.NO
        // 2. Product Name
        // 3. Quantity in units
        // 4. BATCH NO
        // 5. BATCH NO (twice)
        // 6. Remarks/corrective action (if applicable)
        // 7. Driver signature (extra column at the end)
        sheetData.push([
          "S.NO",
          "Product Name",
          "Quantity in units",
          "BATCH NO",
          "BATCH NO",
          "Remarks/corrective action (if applicable)",
        ]);

        // === ITEM ROWS ===
        let serial = 1;
        let totalQuantity = 0;

        // We only want items that this restaurant actually ordered;
        // itemQuantities already has all titles for this restaurant.
        itemQuantities.forEach((iq) => {
          const qty = Number(iq.quantity) || 0;
          if (qty <= 0) return; // skip items with no quantity for this restaurant

          totalQuantity += qty;

          sheetData.push([
            serial,         // S.NO
            iq.title,       // Product Name
            qty,            // Quantity in units
            "",             // BATCH NO
            "",             // BATCH NO (second)
            "",             // Remarks
            "",             // Driver signature
          ]);

          serial += 1;
        });

        // === TOTAL ROW at end ===
        sheetData.push([
          "", // S.NO
          "TOTAL UNIT", // Product Name
          totalQuantity, // Quantity in units total
          "", "", "", "",
        ]);
        sheetData.push([
          "",
          "DRIVER SIGNATURE",
        ]);

        // Convert to worksheet and append with restaurant name as sheet name
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

        // Optional: set some basic column widths for better readability
        const wsCols = [
          { wch: 6 },   // S.NO / empty
          { wch: 30 },  // Restaurant / Product Name / Vehicle No
          { wch: 35 },  // Dispatch/Delivery/Quantity
          { wch: 35 },  // Dates / Batch
          { wch: 40 },  // Doc info / Batch
          { wch: 45 },  // Created by / Remarks
          { wch: 25 },  // Driver signature
        ];
        worksheet["!cols"] = wsCols;

        // Sheet name must be <= 31 chars and no special / \ ? * [ ]
        let safeName = restaurantName.substring(0, 31)
          .replace(/[\\/?*\[\]:]/g, " ");
        if (!safeName.trim()) safeName = "Sheet";

        XLSX.utils.book_append_sheet(workbook, worksheet, safeName);
      });

      XLSX.writeFile(
        workbook,
        `dispatch_notes_${new Date().toISOString()}.xlsx`
      );
    } catch (err) {
      setError("Failed to generate dispatch notes Excel");
      console.error("Dispatch notes Excel error:", err);
    }
  };



  // Process fetched data
  const processData = useCallback(async (invoices, db) => {
    try {
      const [
        inventoryItemsSnapshot,
        categoriesSnapshot,
        usersSnapshot
      ] = await Promise.all([
        getDocs(collection(db, "inventoryItems")),
        getDocs(collection(db, "inventoryCategory")),
        getDocs(collection(db, "users"))
      ]);

      const inventoryItems = inventoryItemsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const categories = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const users = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Process categorized items (all categories, not just uncategorized)
      const categorizedItems = {};
      const uniqueItems = new Set();

      invoices.forEach(invoice => {
        invoice.items.forEach(item => {
          uniqueItems.add(item.title);

          const inventoryItem = inventoryItems.find(
            invItem => invItem.title === item.title
          );
          if (!inventoryItem) return;

          const category = categories.find(
            cat => cat.id === inventoryItem.categoryId
          );
          const categoryName = category?.category || "Uncategorized";

          if (!categorizedItems[categoryName]) {
            categorizedItems[categoryName] = [];
          }

          if (!categorizedItems[categoryName].some(i => i.title === item.title)) {
            categorizedItems[categoryName].push(item);
          }
        });
      });

      // Process restaurant-wise data, skip restaurants with zero orders
      const itemList = Array.from(uniqueItems).map(title => ({ title }));
      const restaurantGrid = users.map(user => {
        const orders = invoices.filter(
          invoice => invoice.restaurantName === user.restaurantName
        );

        if (orders.length === 0) return null; // Skip if no orders

        const itemQuantities = itemList.map(item => {
          const quantity = orders
            .flatMap(order => order.items)
            .filter(orderItem => orderItem.title === item.title)
            .reduce((sum, orderItem) => sum + (Number(orderItem.quantity) || 0), 0);
          return { title: item.title, quantity: Number(quantity) };
        });

        return { restaurantName: user.restaurantName, itemQuantities };
      }).filter(Boolean); // Remove null entries

      return { categorizedItems, restaurantGrid };
    } catch (err) {
      console.error("Error processing data:", err);
      throw err;
    }
  }, []);

  // Apply restaurant filter to data
  const filteredData = useMemo(() => {
    if (selectedRestaurant === "all") return data;
    return data.filter(restaurant => restaurant.restaurantName === selectedRestaurant);
  }, [data, selectedRestaurant]);

  // Fetch data with real-time updates
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const db = getFirestore();
      const { start, end } = getUKDateRange();

      const invoicesQuery = query(
        collection(db, "invoices"),
        where("createdAt", ">=", start),
        where("createdAt", "<=", end)
      );

      const unsubscribe = onSnapshot(invoicesQuery, async (snapshot) => {
        const invoices = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        if (invoices.length === 0) {
          setData([]);
          setItems({});
          setLoading(false);
          setLastUpdated(new Date());
          return;
        }

        try {
          const { categorizedItems, restaurantGrid } = await processData(invoices, db);
          setItems(categorizedItems);
          setData(restaurantGrid);
          setLastUpdated(new Date());
        } catch (err) {
          setError("Failed to process data. Please try again.");
          console.error("Processing error:", err);
        } finally {
          setLoading(false);
        }
      });

      return () => unsubscribe();
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
      console.error("Fetch error:", err);
      setLoading(false);
    }
  }, [processData]);

  // Manual refresh
  const handleRefresh = async () => {
    try {
      setLoading(true);
      await fetchData();
    } catch (err) {
      setError("Refresh failed. Please try again.");
      console.error("Refresh error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Initialize data fetching
  useEffect(() => {
    const unsubscribePromise = fetchData();

    return () => {
      unsubscribePromise.then(unsubscribe => {
        if (unsubscribe && typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    };
  }, [fetchData]);

  // Handle restaurant filter change
  const handleRestaurantFilter = (event) => {
    setSelectedRestaurant(event.target.value);
  };

  const downloadExcel = () => {
    try {
      const workbook = XLSX.utils.book_new();
      const worksheetData = [];

      // Header row
      const headers = ["Item", ...filteredData.map(d => d.restaurantName), "Total"];
      worksheetData.push(headers);

      // Add categories and items
      Object.entries(items).forEach(([category, categoryItems]) => {
        worksheetData.push([category, ...Array(filteredData.length + 1).fill("")]);

        categoryItems.forEach(item => {
          const row = [item.title];
          let rowTotal = 0;

          filteredData.forEach(restaurant => {
            const quantity = restaurant.itemQuantities.find(
              q => q.title === item.title
            )?.quantity || 0;
            row.push(quantity);
            rowTotal += quantity;
          });

          row.push(rowTotal);
          worksheetData.push(row);
        });
      });

      // Grand Total Row
      const totalRow = ["Total"];
      let grandTotal = 0;

      filteredData.forEach(restaurant => {
        const restaurantTotal = restaurant.itemQuantities.reduce(
          (sum, item) => sum + item.quantity,
          0
        );
        totalRow.push(restaurantTotal);
        grandTotal += restaurantTotal;
      });

      totalRow.push(grandTotal);
      worksheetData.push(totalRow);

      // Convert to worksheet and save
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");
      XLSX.writeFile(workbook, `todays_invoices_${new Date().toISOString()}.xlsx`);
    } catch (err) {
      setError("Failed to generate Excel file");
      console.error("Excel generation error:", err);
    }
  };

  if (loading && !lastUpdated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
          <Box mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
            >
              Retry
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  if (!filteredData.length) {
    return (
      <Box p={2}>
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{
            "& .MuiAlert-icon": {
              color: theme.palette.primary.main,
            },
          }}
        >
          <AlertTitle>No Orders Found</AlertTitle>
          There are no orders for the last 3 days. New orders will appear here as they come in.
          <Box mt={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          marginLeft: "3.5%",
          marginTop: "15px",
          maxWidth: "95%",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography
            variant="h4"
            color="primary"
            gutterBottom
            sx={{ fontWeight: "bold" }}
          >
            Today's Invoice Grid
          </Typography>

          {/* Restaurant Filter */}
          <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Restaurant</InputLabel>
            <Select
              value={selectedRestaurant}
              onChange={handleRestaurantFilter}
              label="Filter by Restaurant"
            >
              <MenuItem value="all">
                <em>All Restaurants</em>
              </MenuItem>
              {data.map((restaurant) => (
                <MenuItem key={restaurant.restaurantName} value={restaurant.restaurantName}>
                  {restaurant.restaurantName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" alignItems="center" gap={1}>
            {lastUpdated && (
              <Typography variant="caption" color="textSecondary">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </Typography>
            )}
            <Tooltip title="Refresh data">
              <IconButton onClick={handleRefresh} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Table Container */}
        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "4px",
            },
          }}
        >
          <table
            id="invoice-table"
            style={{
              minWidth: "100%",
              width: "max-content",
              borderCollapse: "collapse",
              borderSpacing: 0,
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    padding: "16px",
                    border: "1px solid #ddd",
                    minWidth: "150px",
                    maxWidth: "250px",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  }}
                >
                  Item
                </th>
                {filteredData.map((restaurant, index) => (
                  <th
                    key={index}
                    style={{
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                      padding: "16px",
                      border: "1px solid #ddd",
                      minWidth: "150px",
                      maxWidth: "200px",
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                    }}
                  >
                    {restaurant.restaurantName}
                  </th>
                ))}
                <th
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    padding: "16px",
                    border: "1px solid #ddd",
                    minWidth: "70px",
                    position: "sticky",
                    right: 0,
                    zIndex: 2,
                  }}
                >
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(items).map(([category, categoryItems]) => (
                <React.Fragment key={category}>
                  {/* Category Divider */}
                  <tr>
                    <td
                      colSpan={filteredData.length + 2}
                      style={{
                        backgroundColor: "#f8f9fa",
                        fontWeight: "bold",
                        textAlign: "left",
                        padding: "10px",
                        border: "1px solid #ddd",
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                      }}
                    >
                      {category}
                    </td>
                  </tr>

                  {/* Render Items in Category */}
                  {categoryItems.map((item) => {
                    const total = filteredData.reduce((sum, restaurant) => {
                      const quantity =
                        restaurant.itemQuantities.find(
                          (q) => q.title === item.title
                        )?.quantity || 0;
                      return sum + quantity;
                    }, 0);

                    return (
                      <tr key={item.title}>
                        <td
                          style={{
                            padding: "16px",
                            border: "1px solid #ddd",
                            fontWeight: "500",
                            position: "sticky",
                            left: 0,
                            backgroundColor: "#fff",
                            zIndex: 1,
                          }}
                        >
                          {item.title}
                        </td>

                        {filteredData.map((restaurant) => (
                          <td
                            key={restaurant.restaurantName}
                            style={{
                              padding: "16px",
                              border: "1px solid #ddd",
                              textAlign: "center",
                            }}
                          >
                            {restaurant.itemQuantities.find(
                              (q) => q.title === item.title
                            )?.quantity || "-"}
                          </td>
                        ))}

                        <td
                          style={{
                            padding: "16px",
                            border: "1px solid #ddd",
                            fontWeight: "bold",
                            textAlign: "center",
                            position: "sticky",
                            right: 0,
                            backgroundColor: "#fff",
                            zIndex: 1,
                          }}
                        >
                          {total}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}

              {/* Grand Total Row */}
              <tr style={{ backgroundColor: "#f1f1f1", fontWeight: "bold" }}>
                <td
                  style={{
                    padding: "16px",
                    border: "1px solid #ddd",
                    position: "sticky",
                    left: 0,
                    backgroundColor: "#fff",
                    zIndex: 2,
                  }}
                >
                  Total
                </td>

                {filteredData.map((restaurant) => {
                  const grandTotal = restaurant.itemQuantities.reduce(
                    (sum, item) => sum + item.quantity,
                    0
                  );
                  return (
                    <td
                      key={restaurant.restaurantName}
                      style={{
                        padding: "16px",
                        border: "1px solid #ddd",
                        textAlign: "center",
                      }}
                    >
                      {grandTotal}
                    </td>
                  );
                })}

                <td
                  style={{
                    padding: "16px",
                    border: "1px solid #ddd",
                    fontWeight: "bold",
                    textAlign: "center",
                    position: "sticky",
                    right: 0,
                    backgroundColor: "#fff",
                    zIndex: 2,
                  }}
                >
                  {filteredData.reduce(
                    (sum, restaurant) =>
                      sum +
                      restaurant.itemQuantities.reduce(
                        (s, i) => s + i.quantity,
                        0
                      ),
                    0
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3, flexWrap: "wrap" }}>
          {/* Existing Excel (grid style) */}
          <Button
            variant="contained"
            color="secondary"
            onClick={downloadExcel}
            sx={{ minWidth: 150 }}
          >
            Download Grid Excel
          </Button>

          {/* Grid PDF */}
          <Button
            variant="contained"
            color="primary"
            onClick={downloadGridPDF}
            sx={{ minWidth: 150 }}
          >
            Download Grid PDF
          </Button>

          {/* New Dispatch Notes Excel */}
          <Button
            variant="contained"
            color="warning"
            onClick={downloadDispatchNotesExcel}
            sx={{ minWidth: 180 }}
          >
            Download Dispatch Notes Excel
          </Button>

          {/* Dispatch Notes PDF */}
          <Button
            variant="contained"
            color="info"
            onClick={downloadDispatchNotesPDF}
            sx={{ minWidth: 180 }}
          >
            Download Dispatch Notes PDF
          </Button>
        </Box>


      </Paper>
    </ThemeProvider>
  );
};

export default TodayInvoicesGrid;
